/**
 * Q1c — N1 · Competência 2 · task-model H3.11.K03
 *
 *   "Dados dois vértices opostos de um retângulo ou quadrado no plano cartesiano,
 *    COMPLETAR uma ficha com as coordenadas dos vértices restantes."
 *
 * É o formato da questão do GeoGebra: além do plano, uma FICHA mostra a figura
 * desenhada à parte, com as coordenadas de A e C escritas junto dos vértices e espaços
 * para escrever as de B e D. O aluno produz a resposta — não a reconhece entre
 * alternativas —, e por isso o modelo é 2PL, sem acerto ao acaso.
 *
 * Os erros típicos são os mesmos da múltipla escolha e da marcação; aqui eles viram
 * DIAGNÓSTICOS: a resposta digitada que coincide com um deles recebe o nome do erro e a
 * conta que o produz.
 */

import type { ConstructionSnapshot } from '../../geom/construction.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { strong, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  CONTEXTOS_QUADRILATERO,
  descricaoDoPlano,
  errosDoQuadrilatero,
  fichaDoQuadrilatero,
  figuraDoPlano,
  relacaoDosDados,
  resolucaoDoQuadrilatero,
  respostasAceitas,
} from '../_comum/quadrado.ts';
import { sortearParams, type Params } from '../h3-11-K01-quadrado/template.ts';

export const quadradoCoordenadas: ItemTemplate<Params, ConstructionSnapshot | null> = {
  id: templateId('h3-11-K03-quadrado-coordenadas'),
  // v2: eixos numerados só na origem e nos extremos.
  version: 2,
  taskModel: taskModelCode('H3.11.K03'),
  skills: [skillCode('H3.11')],
  axis: 'II',
  level: 'N1',
  title: 'Quadrilátero no plano: completar as coordenadas',
  responseModel: '2PL',

  difficultySources: ['steps.2', 'inverse-problem'],

  params: sortearParams,
  solve: () => null,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.nivel === 'inclinado') fontes.push('steps.3plus', 'non-round-numbers');
    return fontes;
  },

  build(p) {
    const ctx = CONTEXTOS_QUADRILATERO[p.contexto] as (typeof CONTEXTOS_QUADRILATERO)[number];
    const rel = relacaoDosDados(p.nivel);
    const figura = p.nivel === 'retangulo' ? 'retângulo' : 'quadrado';
    const ficha = fichaDoQuadrilatero(p.q);
    return {
      stem: [txt(`${ctx.abertura(p.nivel)} ${rel.antes}`), strong(rel.destaque), txt(rel.depois)],
      comando: [
        txt(
          `Na ficha, o ${figura} ABCD aparece desenhado à parte, com as coordenadas de A e de C. Complete-a com as coordenadas dos vértices `,
        ),
        strong('B e D'),
        txt('.'),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'plano',
          scene: figuraDoPlano(p.q),
          alt: descricaoDoPlano(p.q),
        },
      ],
      interaction: {
        kind: 'coordinate-entry',
        ficha: ficha.cena,
        pontos: ficha.pontos,
        respostas: respostasAceitas(p.q),
        erros: errosDoQuadrilatero(p.q).map((e) => ({
          pontos: e.pontos,
          error: e.error,
          explanation: e.explicacao,
        })),
      },
      rationale: [strong('Resolução. '), ...resolucaoDoQuadrilatero(p.q)],
      declared: p.nivel === 'retangulo' ? 'facil' : 'dificil',
    };
  },
};
